
import { useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';

export const useAppTimeTracking = () => {
  const { logAction } = useAnalytics();
  const sessionStartRef = useRef<number>(0);
  const isActiveRef = useRef<boolean>(true);
  const totalTimeRef = useRef<number>(0);

  useEffect(() => {
    // Start timing when the hook mounts
    sessionStartRef.current = Date.now();
    
    // Log app session start
    logAction('app_session_start', {
      session_start: new Date().toISOString(),
      user_agent: navigator.userAgent,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight
    });

    // Track when user becomes active/inactive
    const handleVisibilityChange = () => {
      const now = Date.now();
      
      if (document.hidden) {
        // User switched away from the app
        if (isActiveRef.current) {
          const sessionTime = now - sessionStartRef.current;
          totalTimeRef.current += sessionTime;
          isActiveRef.current = false;
          
          logAction('app_session_pause', {
            session_duration: Math.round(sessionTime / 1000), // Convert to seconds
            total_time: Math.round(totalTimeRef.current / 1000), // Convert to seconds
            paused_at: new Date().toISOString()
          });
        }
      } else {
        // User returned to the app
        sessionStartRef.current = now;
        isActiveRef.current = true;
        
        logAction('app_session_resume', {
          resumed_at: new Date().toISOString(),
          total_time: Math.round(totalTimeRef.current / 1000) // Convert to seconds
        });
      }
    };

    // Track when user leaves/returns to the page
    const handleBeforeUnload = () => {
      if (isActiveRef.current) {
        const sessionTime = Date.now() - sessionStartRef.current;
        totalTimeRef.current += sessionTime;
        
        // Use sendBeacon for reliable tracking on page unload
        const data = {
          action_type: 'app_session_end',
          metadata: {
            session_duration: Math.round(sessionTime / 1000), // Convert to seconds
            total_time: Math.round(totalTimeRef.current / 1000), // Convert to seconds
            ended_at: new Date().toISOString()
          }
        };
        
        // Try to send the data before the page unloads
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/analytics', JSON.stringify(data));
        } else {
          // Fallback for browsers that don't support sendBeacon
          logAction('app_session_end', {
            session_duration: Math.round(sessionTime / 1000), // Convert to seconds
            total_time: Math.round(totalTimeRef.current / 1000), // Convert to seconds
            ended_at: new Date().toISOString()
          });
        }
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Final session tracking
      if (isActiveRef.current) {
        const sessionTime = Date.now() - sessionStartRef.current;
        totalTimeRef.current += sessionTime;
        
        logAction('app_session_end', {
          session_duration: Math.round(sessionTime / 1000), // Convert to seconds
          total_time: Math.round(totalTimeRef.current / 1000), // Convert to seconds
          ended_at: new Date().toISOString()
        });
      }
    };
  }, [logAction]);

  // Return current session info (optional, for debugging)
  return {
    getCurrentSessionTime: () => {
      if (isActiveRef.current) {
        return Math.round((Date.now() - sessionStartRef.current) / 1000); // Convert to seconds
      }
      return 0;
    },
    getTotalTime: () => Math.round(totalTimeRef.current / 1000), // Convert to seconds
    isActive: () => isActiveRef.current
  };
};
